export type BlockType = 'structure' | 'media' | 'component'

export interface EditorBlock {
  id: string
  label: string
  icon: string
  type: BlockType
  description?: string
}

export interface BlockConfigData {
  // Common
  align?: 'left' | 'center' | 'right'
  padding?: string
  backgroundColor?: string
  blockSpacing?: string // Spacing after this block (email-safe padding)
  border?: string // Border for entire block (e.g., "1px solid #ddd")
  
  // Text / Content
  title?: string
  subtitle?: string
  content?: string
  iconSymbol?: string
  iconSize?: string
  iconWidth?: string
  iconColor?: string
  titleColor?: string
  contentColor?: string
  
  // Multi-column content
  col1Content?: string
  col2Content?: string
  col3Content?: string
  
  // Column widths (in pixels or percentage)
  col1Width?: string
  col2Width?: string
  col3Width?: string
  
  // Per-column styling
  col1BackgroundColor?: string
  col2BackgroundColor?: string
  col3BackgroundColor?: string
  
  col1Padding?: string
  col2Padding?: string
  col3Padding?: string
  
  col1Border?: string
  col2Border?: string
  col3Border?: string

  // Per-column text alignment
  col1Align?: 'left' | 'center' | 'right'
  col2Align?: 'left' | 'center' | 'right'
  col3Align?: 'left' | 'center' | 'right'

  
  // Media
  imageUrl?: string
  imageAlt?: string
  imageWidth?: string // Width in pixels or percentage (e.g., "300px", "100%")
  imageHeight?: string // Height in pixels or auto (e.g., "200px", "auto")
  imagePosition?: 'left' | 'right' // For image_text block
  imageBorderRadius?: string // Border radius for image_text (e.g., "4px", "8px", "50%")
  
  // Spacer
  spacerHeight?: string // Height of spacer in pixels (e.g., "40px")
  
  // Common Styling
  paddingTop?: string
  paddingBottom?: string
  paddingLeft?: string
  paddingRight?: string
  marginTop?: string
  marginBottom?: string
  borderRadius?: string
  fontSize?: string
  color?: string // Text color
  borderColor?: string
  borderWidth?: string
  borderStyle?: string
  
  // Header
  logoUrl?: string // Logo image URL
  logoWidth?: string // Logo width (e.g., "150px")
  logoHeight?: string // Logo height (e.g., "auto")
  
  // Footer
  copyrightText?: string // Copyright text (e.g., "© 2026 Company Name")
  unsubscribeText?: string // Unsubscribe link text (e.g., "Cancelar suscripción")
  
  // Social Media
  socialLayout?: 'horizontal' | 'vertical'
  socialFb?: string
  socialFbText?: string
  socialIg?: string
  socialIgText?: string
  socialIn?: string
  socialInText?: string
  socialX?: string
  socialXText?: string
  socialYt?: string
  socialYtText?: string
  socialWa?: string
  socialWaText?: string
  socialSize?: string // Icon size (e.g., "24px")
  
  // Action
  buttonText?: string
  buttonUrl?: string
  buttonColor?: string
  buttonWidth?: string
  buttonStyle?: 'solid' | 'outline'
  buttonPlacement?: 'above' | 'below' // For centered layout
  buttonPaddingVertical?: string
  buttonPaddingHorizontal?: string
  
  // Structure
  columns?: number

  // Feature List
  featureCount?: number
  featureLayout?: 'horizontal' | 'vertical'
  featureIconColor?: string
  featureIconSize?: string
  feature1Icon?: string
  feature1Title?: string
  feature1Text?: string
  feature2Icon?: string
  feature2Title?: string
  feature2Text?: string
  feature3Icon?: string
  feature3Title?: string
  feature3Text?: string
  feature4Icon?: string
  feature4Title?: string
  feature4Text?: string

  // Card Grid
  cardCount?: number
  card1Image?: string
  card1Title?: string
  card1Text?: string
  card1ButtonText?: string
  card1ButtonUrl?: string
  card2Image?: string
  card2Title?: string
  card2Text?: string
  card2ButtonText?: string
  card2ButtonUrl?: string
  card3Image?: string
  card3Title?: string
  card3Text?: string
  card3ButtonText?: string
  card3ButtonUrl?: string

  // Contact Card
  headerTitle?: string
  headerBackgroundColor?: string
  headerTitleColor?: string
  contactTitle?: string
  contactTitleColor?: string

  // Testimonial
  quoteText?: string
  quoteAuthor?: string
  quoteRole?: string
  quoteAvatarUrl?: string
  quoteStyle?: 'simple' | 'card'
}

export interface BlockInstance {
  id: string // Unique instance ID
  blockId: string // Reference to BLOCKS_LIBRARY id
  data: BlockConfigData
  locked?: boolean
  lockReason?: string
}

export const BLOCKS_LIBRARY: EditorBlock[] = [
  // Structure
  { id: '1_col', label: '1 Column', icon: 'bi-square', type: 'structure', description: 'Full width section' },
  { id: '2_cols', label: '2 Columns', icon: 'bi-layout-split', type: 'structure', description: 'Two equal columns' },
  { id: '3_cols', label: '3 Columns', icon: 'bi-layout-three-columns', type: 'structure', description: 'Three equal columns' },
  { id: 'hero', label: 'Hero Section', icon: 'bi-card-image', type: 'structure', description: 'Large image with title and CTA' },
  { id: 'image_text', label: 'Image | Text', icon: 'bi-layout-sidebar', type: 'structure', description: 'Image + text side by side. Choose position and width.' },
  
  // Header & Footer
  { id: 'social_links', label: 'Social Links', icon: 'bi-share', type: 'structure', description: 'Social media icons and links' },
  { id: 'icon_note', label: 'Icon Note', icon: 'bi-exclamation-triangle', type: 'component', description: 'Icon, title and supporting text' },
  { id: 'feature_list', label: 'Feature List', icon: 'bi-list-check', type: 'structure', description: 'Icon + title + description items in a row' },
  { id: 'card_grid', label: 'Card Grid', icon: 'bi-grid', type: 'structure', description: '2 or 3 cards with image, title, text and optional button' },
  { id: 'contact_card', label: 'Contact Card', icon: 'bi-person-vcard', type: 'component', description: 'Styled card with header and vertical social links' },
  { id: 'testimonial', label: 'Testimonial', icon: 'bi-chat-quote', type: 'component', description: 'Quote with author name and role' },

  // Components
  { id: 'button', label: 'Button', icon: 'bi-box-arrow-in-right', type: 'component', description: 'Call to Action button' },
  { id: 'divider', label: 'Divider', icon: 'bi-hr', type: 'component', description: 'Horizontal line' },
  { id: 'spacer', label: 'Spacer', icon: 'bi-arrow-down-up', type: 'component', description: 'Vertical space' },
  
  // Media
  { id: 'image', label: 'Image', icon: 'bi-image', type: 'media', description: 'Single image' },
]
